import * as api from "../../lib/api.js";
import * as store from "../../lib/store.js";
import { t } from "../contract/strings";
import { useApp, type AppState } from "../state/store";
import { rotationSettled } from "./token-rotation";

/**
 * The server stopped accepting this browser's sign-in (#854): the account was deleted, or
 * the sign-in was revoked, expired or signed out elsewhere. The server says only 401 --
 * deletion removes the sign-in rows, so a deleted account's cookie looks like any other
 * unknown one -- and every reason is handled the same way.
 *
 * What happens is what Sign out does on this device, minus the server calls (there is
 * nothing left there to revoke): the voice profile, keys and device token are wiped, the
 * account is cleared, and sign-in opens with one plain line. The guards do the navigation:
 * with no account and no identity every screen redirects to the front door.
 *
 * A 401 from any other call is only a hint: the device token may be what was refused, or
 * one route may refuse for its own reasons. So nothing is wiped on a hint alone; `/me` is
 * asked first, and only its 401 ends the sign-in. A `/me` that cannot be reached changes
 * nothing.
 */

/** A meeting this device is starting, in, or still sending: the wipe waits for it to end. */
export function meetingInProgress(state: Pick<AppState, "session" | "phase">): boolean {
  if (!state.session) return false;
  return state.phase === "idle" || state.phase === "recording" || state.phase === "wrapping";
}

/** The least time between two foreground re-checks of `/me`. */
export const FOREGROUND_CHECK_MIN_MS = 30_000;

let checking: Promise<void> | null = null;
let ending: Promise<void> | null = null;
let lastForegroundCheck = 0;

/**
 * Ask the server whether the sign-in is still good, and end it here if it is not. Only
 * while an account is showing; a second call while one is out joins it, which is also what
 * keeps the `/me` 401 this makes from asking again.
 */
export function recheckAccount(): Promise<void> {
  if (!useApp.getState().account) return Promise.resolve();
  if (checking) return checking;
  checking = (async () => {
    let account: Awaited<ReturnType<typeof api.account>> | undefined;
    try {
      account = await api.account();
    } catch {
      return; // could not ask: nothing is known, so nothing changes
    }
    if (account === null && useApp.getState().account) await endSignIn();
  })().finally(() => {
    checking = null;
  });
  return checking;
}

/** Back in the foreground: re-check, at most once per `FOREGROUND_CHECK_MIN_MS`. */
export function recheckOnForeground(now: number = Date.now()): Promise<void> {
  if (now - lastForegroundCheck < FOREGROUND_CHECK_MIN_MS) return Promise.resolve();
  lastForegroundCheck = now;
  return recheckAccount();
}

/**
 * Boot found no account. A browser that still holds a device was signed in once; if its
 * device token is refused too, the sign-in it had is gone (a deleted account revokes every
 * device), so it is wiped and told. A device the server still accepts is left alone: an
 * expired cookie alone is not a reason to erase someone's voice profile.
 */
export async function checkDeviceAfterSignedOutBoot(): Promise<void> {
  if (useApp.getState().account || !useApp.getState().credentials) return;
  try {
    await api.deviceMe();
  } catch (err) {
    if ((err as { status?: number }).status === 401) await endSignIn();
  }
}

/**
 * Wipe this device as Sign out does and send it to sign-in with `account.session_ended`.
 * Once only: a call while one is running joins it. A meeting in progress ends first, by
 * its own path; the wipe runs when it has.
 */
export function endSignIn(): Promise<void> {
  if (ending) return ending;
  ending = (async () => {
    await meetingEnded();
    await rotationSettled();
    api.setToken(null);
    try {
      await store.wipeAll();
    } finally {
      const app = useApp.getState();
      app.setAccount(null);
      app.setIdentity({ credentials: null, template: null, keypair: null });
      app.setDeviceAccountLink({ status: "not_applicable", reason: "no_device" });
      app.setScreenStatus({ path: "/signin", message: t("account.session_ended"), kind: "ok" });
    }
  })().finally(() => {
    ending = null;
  });
  return ending;
}

function meetingEnded(): Promise<void> {
  if (!meetingInProgress(useApp.getState())) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = useApp.subscribe((state) => {
      if (meetingInProgress(state)) return;
      unsubscribe();
      resolve();
    });
  });
}

/**
 * Wire the hints in: a 401 on any call, and the page coming back into view. Called once at
 * boot.
 */
export function watchForEndedSignIn(): void {
  api.setUnauthorizedHandler(() => {
    void recheckAccount();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void recheckOnForeground();
  });
}

/** Test seam: forget the latches and the foreground clock. */
export function resetEndedSignInForTests(): void {
  checking = null;
  ending = null;
  lastForegroundCheck = 0;
}
